import { useParams, Link, useNavigate } from 'react-router-dom';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

export default function ArticlePage() {
  const { movieId = '' } = useParams<{ movieId: string }>();
  const navigate = useNavigate();
  const movie = getMovieById(movieId);

  if (!movie) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
            <i className="ri-ghost-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">影片未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">该影片可能已下架或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie.id, movie.category, 6);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            alternateName: movie.englishTitle,
            description: movie.description,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.cast.map((name) => ({
              '@type': 'Person',
              name,
            })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
              ratingCount: Math.floor(Math.random() * 50000) + 10000,
            },
          }),
        }}
      />

      <article className="min-h-screen pt-16">
        <nav className="px-4 md:px-6 py-4 bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            <Link to={`/category/${movie.category}`} className="text-foreground-500 hover:text-primary-500 transition-colors">
              {movie.subCategory}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-800 font-medium truncate max-w-[200px]">{movie.title}</span>
          </div>
        </nav>

        <section className="relative h-[280px] md:h-[400px] w-full overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${movie.backdrop})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/40 to-background-950/30"></div>
        </section>

        <section className="px-4 md:px-6 -mt-32 md:-mt-48">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-40 md:w-56 aspect-[3/4] rounded-xl overflow-hidden shadow-lg relative z-10">
                  <img
                    src={movie.poster}
                    alt={`${movie.title}电影海报 - 小草影院`}
                    title={`${movie.title} - ${movie.subCategory}在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="relative z-10 flex-1 text-center md:text-left pt-4 md:pt-16">
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3">
                  <span className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                    {movie.subCategory}
                  </span>
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-50/15 text-background-100 text-xs rounded-full border border-background-300/20 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                  {movie.title}
                  <span className="text-lg md:text-xl text-background-300 ml-3 font-normal">{movie.englishTitle}</span>
                </h1>

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-background-200 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400"></i>
                    <strong>{movie.rating}</strong>/10
                  </span>
                  <span>{movie.year}</span>
                  <span>{movie.duration}</span>
                  <span>{movie.region}</span>
                  <span>{movie.releaseDate} 上映</span>
                </div>

                <p className="text-sm text-background-200/80 max-w-2xl leading-relaxed">
                  {movie.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
              <div className="lg:col-span-2">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                  剧情简介
                </h2>
                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: movie.content }}
                />

                <div className="mt-10 p-6 bg-background-100 rounded-xl">
                  <h3 className="text-lg font-heading font-bold text-foreground-800 mb-4">
                    影片信息
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 flex-shrink-0">导演：</span>
                      <span className="text-foreground-800 font-medium">{movie.director}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 flex-shrink-0">主演：</span>
                      <span className="text-foreground-800 font-medium">{movie.cast.join(' / ')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 flex-shrink-0">类型：</span>
                      <span className="text-foreground-800 font-medium">{movie.subCategory}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 flex-shrink-0">地区：</span>
                      <span className="text-foreground-800 font-medium">{movie.region}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 flex-shrink-0">片长：</span>
                      <span className="text-foreground-800 font-medium">{movie.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground-500 flex-shrink-0">上映：</span>
                      <span className="text-foreground-800 font-medium">{movie.releaseDate}</span>
                    </div>
                  </div>
                </div>
              </div>

              <aside>
                <div className="sticky top-24">
                  <h3 className="text-lg font-heading font-bold text-foreground-800 mb-4">
                    相关推荐
                  </h3>
                  <div className="space-y-3" data-product-shop="true">
                    {relatedMovies.map((rm) => (
                      <Link
                        key={rm.id}
                        to={`/article/${rm.id}`}
                        className="flex gap-3 p-3 bg-background-50 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <div className="w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={rm.poster}
                            alt={rm.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-800 truncate font-heading">
                            {rm.title}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-1 flex items-center gap-1">
                            <i className="ri-star-fill text-accent-400 text-xs"></i>
                            {rm.rating}
                          </p>
                          <p className="text-xs text-foreground-400 mt-0.5">
                            {rm.year} / {rm.subCategory}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>

                  <Link
                    to={`/category/${movie.category}`}
                    className="inline-flex items-center gap-2 mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    更多{movie.subCategory}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto text-center">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 hover:bg-background-200 border border-background-300 text-foreground-700 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回上一页
            </button>
          </div>
        </section>
      </article>
    </>
  );
}